import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import {  BehaviorSubject, combineLatest, Subscription, take } from 'rxjs';
import { findKey, isEmpty } from 'lodash';
import { MessageService } from 'primeng/api';

import { MentionsApi } from '../../../../api/mentions/mentions.api';

import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { languageDataLocalizationMap } from '../../../../Core/Localization/language-localization.model';

import { BulkMentionRequestModel } from '../../../../Monitor/Shared/models/bulk-mention-request-model';
import { MonitoringBulkActionSourceMethodEnum } from '../../../../Monitor/Shared/enums/monitoring-bulk-action-source-method.enum';
import { BulkMentionActionModel } from '../../../../Monitor/Shared/models/bulk-mention-action.model';
import { MonitoringFileTypesEnum } from '../../../../Monitor/Shared/enums/monitoring-file-types.enum';

import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { bcp47LanguageMap } from '../../../Forms/data/bcp47-language-tags';

import { TranslateMentionsService } from '../../services/translate-mentions.service';
import { Mention } from '../../models/mention';
import { translationLanguageOptions } from '../../../Forms/data/translation-language-options';

enum TranslateTabIds {
    ORIGINAL,
    TRANSLATED
}

@Component({
    selector: 'agpr-mention-language-display',
    templateUrl: './mention-language-display.component.html',
    styleUrls: ['./mention-language-display.component.scss'],
    standalone: false
})
export class MentionLanguageDisplayComponent implements OnDestroy, OnInit {
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;

    @Input() id: string;
    @Input() selectedTabIndex: number = 0;
    @Input() mention: Mention;
    @Input() showLanguageLabel: boolean = false;
    @Input() openAiPermission: boolean = false;
    @Input() autoSave: boolean = false;
    @Input() showActions: boolean = false;
    @Input() isEditMention: boolean = false;
    @Input() isHalfWidth: boolean = false;

    @Output() tabChanged: EventEmitter<number> = new EventEmitter<number>();
    @Output() languageChanged: EventEmitter<DropdownOptionModel> = new EventEmitter<DropdownOptionModel>();

    tabIds = TranslateTabIds;
    selectedLanguage: DropdownOptionModel;
    defaultLanguage: DropdownOptionModel;

    defaultLanguages: DropdownOptionModel[] = translationLanguageOptions;

    protected languageSuggestions$: BehaviorSubject<DropdownOptionModel[]> = new BehaviorSubject(this.defaultLanguages);

    readonly maxConcurrentTranslation: number = 3;
    concurrentTranslation: number = 0;

    subscriptions: Subscription[] = [];

    constructor(
        public mentionsApi: MentionsApi,
        protected translateService: TranslateMentionsService,
        private currentUserStateService: CurrentUserStateService,
        private messageService: MessageService,
        private localizationService: LocalizationService,
    ) {}

    ngOnInit(){
        this.createSubscriptions();
    }

    private createSubscriptions(): void {
        this.subscriptions.push(
            combineLatest([
                this.currentUserStateService.getDataReferenceLanguageTag(),
                this.currentUserStateService.getUILanguageTag()
            ]).subscribe(([refLang, uiLang]) => {
                this.defaultLanguages.forEach(option => {
                    option.label = languageDataLocalizationMap[uiLang + '.' + option.subLabel];
                });

                this.selectedLanguage = this.defaultLanguages.find(option => option.subLabel === refLang);
                this.defaultLanguage = {...this.selectedLanguage};
            }),
            this.translateService.getConcurrentTranslation().subscribe(
                (concurrentTranslation) => this.concurrentTranslation = concurrentTranslation
            )
        );
    }

    /**
     * after the user types in a value for language translation.. we check the isoLanguage names to see if there is a match
     */
    updateListAfterString(searchString: string) {
        this.selectedLanguage = new DropdownOptionModel(searchString, searchString);

        const subLabel = findKey(bcp47LanguageMap, (value) => {
            return value.toLowerCase() === searchString.toLowerCase();
        });

        if (subLabel) {
            this.selectedLanguage.subLabel = subLabel;
        }
        this.languageChanged.emit(this.selectedLanguage);
    }

    onLanguageBlur() {
        if (isEmpty(this.selectedLanguage.value)) {
            this.selectedLanguage = {...this.defaultLanguage};
            this.languageChanged.emit(this.selectedLanguage);
        }
    }

    translate() {
        if (this.concurrentTranslation < this.maxConcurrentTranslation) {

            this.translateService.setupChatAI(this.mention, this.selectedLanguage, false, this.autoSave);

            // because the translated tab is disabled just before this, it seems to ignore switching tabs automatically, the setTimeout fixes it
            setTimeout(() => {
                this.selectedTabIndex = 1;
                this.tabChanged.emit(this.selectedTabIndex);
            }, 0);
        }
    }

    removeTranslated() {
        if (this.mention.mentionId) {
            const modifications = new BulkMentionActionModel({
                removeFile: MonitoringFileTypesEnum.TRANSLATION
            });
            const bulkMentionRequest = new BulkMentionRequestModel(
                MonitoringBulkActionSourceMethodEnum.SELECTION,
                modifications,
                null,
                [this.mention.mentionId]
            );

            this.mention.translating = true;

            if (this.mention.isTranslatedPreferred) {
                this.setTranslatedPreferred();
            }

            this.mentionsApi.updateMentionsInBulk(bulkMentionRequest.toApi()).pipe(take(1)).subscribe({
                next: () => {
                    this.mention.translating = false;
                    this.selectedTabIndex = 0;
                    delete this.mention.translation;
                },
                error: (error) => {
                    this.mention.translating = false;
                    this.messageService.add(
                        {
                            severity: messageSeverityEnum.error,
                            summary: this.localizationService.transform('Mention.translate.removingTranslation.error.title'),
                            detail: this.localizationService.transform('Mention.translate.removingTranslation.error.description')
                        }
                    );
                }
            });
        }
    }

    setTranslatedPreferred() {
        this.mention.isTranslatedPreferred = !this.mention.isTranslatedPreferred;
        if (this.autoSave) {
            this.mention.translating = true;
            this.mention.showTranslated = false;
            this.mentionsApi.patchMention(this.mention.toUpdateMentionTranslationApiModel()).subscribe({
                next: () => {
                    this.mention.translating = false;
                },
                error: () => {
                    this.messageService.add(
                        {
                            severity: messageSeverityEnum.error,
                            summary: this.localizationService.transform('Mention.translate.setPreferred.error.title'),
                            detail: this.localizationService.transform('Mention.translate.setPreferred.error.description')
                        }
                    );
                    this.mention.translating = false;
                    this.mention.isTranslatedPreferred = !this.mention.isTranslatedPreferred;
                }
            });
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }
}
